import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { getApiUrl } from '../config';
import './Forecast.css';

const Forecast = ({ config }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forecastData, setForecastData] = useState(null);
  const [timeHorizon, setTimeHorizon] = useState('6'); // months
  const [accountFilter, setAccountFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadForecastData();
  }, [config, timeHorizon, accountFilter, startDate, endDate]);

  const loadForecastData = async () => {
    setLoading(true);
    setError('');

    try {
      // Calculate date range
      const today = new Date();
      const start = startDate || today.toISOString().split('T')[0];
      let end;

      if (endDate) {
        end = endDate;
      } else {
        const futureDate = new Date(today);
        futureDate.setMonth(futureDate.getMonth() + parseInt(timeHorizon));
        end = futureDate.toISOString().split('T')[0];
      }

      const period = `${start}..${end}`;

      // Get forecast balance data
      const balanceArgs = `--forecast -p "${period}" --output-format=json${accountFilter ? ` ${accountFilter}` : ''}`;

      const [balanceResponse, registerResponse] = await Promise.all([
        axios.post(getApiUrl('/api/hledger/command'), {
          journalPath: config.journalPath,
          command: `balance ${balanceArgs} --monthly`
        }),
        axios.post(getApiUrl('/api/hledger/command'), {
          journalPath: config.journalPath,
          command: `register ${balanceArgs}`
        })
      ]);

      const balanceData = JSON.parse(balanceResponse.data.stdout || '[]');
      const registerData = JSON.parse(registerResponse.data.stdout || '[]');

      setForecastData({
        balance: balanceData,
        register: registerData,
        period: { start, end }
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading forecast data:', err);
      setError('Error loading forecast data: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const processChartData = () => {
    if (!forecastData || !forecastData.balance) return null;

    // Extract account balances over time
    const accounts = {};
    const months = new Set();

    // Process balance data to get month-by-month account balances
    if (forecastData.balance.length > 0) {
      forecastData.balance.forEach(item => {
        if (item.account && item.amounts) {
          item.amounts.forEach(amount => {
            const account = item.account;
            if (!accounts[account]) {
              accounts[account] = {};
            }
            // Extract month from amount period if available
            const value = parseFloat(amount.amount || amount);
            accounts[account][amount.date || 'total'] = value;
            if (amount.date) months.add(amount.date);
          });
        }
      });
    }

    return { accounts, months: Array.from(months).sort() };
  };

  const renderChart = () => {
    const chartData = processChartData();
    if (!chartData || chartData.months.length === 0) {
      return <div className="no-data">No forecast data available. Make sure you have periodic transactions defined in your journal.</div>;
    }

    // Simple text-based visualization for now
    // TODO: Add proper charting library later
    return (
      <div className="forecast-chart">
        <p className="chart-placeholder">Chart visualization will be added here</p>
        <p className="chart-info">Forecasting from {forecastData.period.start} to {forecastData.period.end}</p>
      </div>
    );
  };

  const renderTable = () => {
    if (!forecastData || !forecastData.register) {
      return <div className="no-data">No forecast data available</div>;
    }

    const registerData = forecastData.register;

    // Group by month
    const monthlyData = {};
    registerData.forEach(entry => {
      if (entry.tdate) {
        const month = entry.tdate.substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = {
            income: 0,
            expenses: 0,
            transactions: []
          };
        }

        // Calculate income vs expenses based on account names
        if (entry.tpostings) {
          entry.tpostings.forEach(posting => {
            const account = posting.paccount || '';
            const amount = posting.pamount && posting.pamount.length > 0
              ? parseFloat(posting.pamount[0].aquantity || 0)
              : 0;

            if (account.toLowerCase().includes('income') || account.toLowerCase().includes('revenue')) {
              monthlyData[month].income += Math.abs(amount);
            } else if (account.toLowerCase().includes('expense') || account.toLowerCase().includes('cost')) {
              monthlyData[month].expenses += Math.abs(amount);
            }
          });
        }

        monthlyData[month].transactions.push(entry);
      }
    });

    const months = Object.keys(monthlyData).sort();

    return (
      <div className="forecast-table-container">
        <table className="forecast-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Projected Income</th>
              <th>Projected Expenses</th>
              <th>Net Change</th>
              <th>Transactions</th>
            </tr>
          </thead>
          <tbody>
            {months.map(month => {
              const data = monthlyData[month];
              const net = data.income - data.expenses;
              return (
                <tr key={month}>
                  <td>{new Date(month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</td>
                  <td className="amount income">{formatCurrency(data.income)}</td>
                  <td className="amount expense">{formatCurrency(data.expenses)}</td>
                  <td className={`amount ${net >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(net)}
                  </td>
                  <td>{data.transactions.length}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading forecast data...</p>
      </div>
    );
  }

  return (
    <div className="forecast-page">
      <div className="page-header">
        <h1>Forecast</h1>
        <p>Project future balances based on recurring transactions</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Forecast Settings</h2>
        </div>
        <div className="card-body">
          <div className="filter-grid">
            <div className="form-group">
              <label className="form-label">Time Horizon</label>
              <select
                className="form-select"
                value={timeHorizon}
                onChange={(e) => setTimeHorizon(e.target.value)}
              >
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">12 months</option>
                <option value="24">24 months</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Account Filter (optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., assets or income or expenses"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Custom Start Date (optional)</label>
              <input
                type="date"
                className="form-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Custom End Date (optional)</label>
              <input
                type="date"
                className="form-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Balance Projection</h2>
        </div>
        <div className="card-body">
          {renderChart()}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Monthly Breakdown</h2>
        </div>
        <div className="card-body">
          {renderTable()}
        </div>
      </div>
    </div>
  );
};

export default Forecast;
